// ═══════════════════════════════════════════════════════════════════════════════
// AgentBriefingTab — "Patient Briefing" tab for an agent
// ═══════════════════════════════════════════════════════════════════════════════
//
// PURPOSE
//   Drop-in component for the per-agent "Patient Briefing" surface. Lists every
//   briefing linked to the agent, ordered by SortOrder (lower wins element-code
//   dedup at render time). Supports:
//     - Link a briefing (drawer picker, multi-add allowed by repeated invocation)
//     - Per-link kill switch (soft-disable, row preserved)
//     - Drag-via-arrows reorder
//     - Hard unlink (confirm + delete)
//     - Master "Disable all" panic button (flips IsActive=false on every row)
//
// SHAPES
//   The /Search endpoint returns AgentBriefingLink rows only. To render code +
//   name + draft/published status, we cross-reference the briefings catalog
//   (one extra fetch on mount, then in-memory join). Keeps the link endpoint
//   small and avoids changing the runtime read shape.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  BulbOutlined,
  CheckOutlined,
  DeleteOutlined,
  EyeOutlined,
  HolderOutlined,
  PlusOutlined,
  PoweroffOutlined,
  ReadOutlined,
  ReloadOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { useStore } from '@/stores';
import { colors, typography } from '@/theme/tokens';
import { LivePreviewDrawer } from './LivePreviewDrawer';
import {
  disableAllBriefingsForAgent,
  linkBriefingToAgent,
  listBriefings,
  listLinksForAgent,
  reorderLinks,
  setLinkActive,
  suggestBriefingsForAgent,
  unlinkBriefingFromAgent,
  type AgentBriefingLink,
  type BriefingContext,
  type BriefingSuggestion,
  type BriefingSuggestionResult,
} from '@/api/briefingContext';

const { Text, Title } = Typography;

export interface AgentBriefingTabProps {
  /** ai.AiAgent.Id — the agent whose links we are managing. */
  agentId: number;
}

/**
 * Composite row shape: link row joined with briefing metadata. Built
 * client-side from two parallel fetches.
 */
interface JoinedRow extends AgentBriefingLink {
  briefingCode?: string;
  briefingName?: string;
  briefingIsDraft?: boolean;
  briefingIsActive?: boolean;
  briefingVersion?: number;
  briefingMerchantId?: number | null;
}

export const AgentBriefingTab = observer(function AgentBriefingTab({ agentId }: AgentBriefingTabProps) {
  const [rows, setRows] = useState<JoinedRow[]>([]);
  const [briefings, setBriefings] = useState<BriefingContext[]>([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // The drawer's patient search runs against whichever merchant the SME has
  // selected in the header chip — that's the merchant whose patients they
  // can see in Studio. The backend additionally enforces "patient must be
  // on the agent's merchant" when the agent is private, so even if the
  // chip happens to point at a different merchant, the preview won't leak
  // PHI across tenants.
  const { merchantContextStore } = useStore();
  const merchantIdForPreview = merchantContextStore.selectedMerchantId;

  async function load() {
    if (!agentId) return;
    setLoading(true);
    try {
      const [links, allBriefings] = await Promise.all([
        listLinksForAgent(agentId, true),
        listBriefings({ includeInactive: true, includeDrafts: false }),
      ]);
      const byId = new Map(allBriefings.map((b) => [b.id, b]));
      const joined: JoinedRow[] = links.map((l) => {
        const b = byId.get(l.briefingContextId);
        return {
          ...l,
          briefingCode: b?.code,
          briefingName: b?.name,
          briefingIsDraft: b?.isDraft,
          briefingIsActive: b?.isActive,
          briefingVersion: b?.version,
          briefingMerchantId: b?.merchantId ?? null,
        };
      });
      setRows(joined);
      setBriefings(allBriefings);
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to load briefing links');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => r.isActive).length;
    const orphanedDraft = rows.filter(
      (r) => r.isActive && (r.briefingIsDraft || !r.briefingIsActive),
    ).length;
    return { total, active, orphanedDraft };
  }, [rows]);

  // ── Actions ──────────────────────────────────────────────────────────────

  async function handleAdd(briefingId: number) {
    try {
      const sortOrder = (rows[rows.length - 1]?.sortOrder ?? 0) + 10;
      await linkBriefingToAgent(agentId, briefingId, sortOrder);
      message.success('Briefing linked');
      setAddOpen(false);
      await load();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Link failed');
    }
  }

  async function handleToggleActive(row: JoinedRow, next: boolean) {
    try {
      await setLinkActive(row.id, next);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isActive: next } : r)));
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to update');
    }
  }

  async function handleUnlink(row: JoinedRow) {
    try {
      await unlinkBriefingFromAgent(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      message.success(`Unlinked '${row.briefingCode ?? row.briefingContextId}'`);
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Unlink failed');
    }
  }

  function handleDisableAll() {
    Modal.confirm({
      title: 'Disable patient briefing for this agent?',
      content: (
        <>
          <Text>
            This is the on-call panic button: it flips IsActive=false on every linked briefing
            for this agent in one step.
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12.5 }}>
            The links are preserved — re-enable later from this same tab. No briefings are deleted.
          </Text>
        </>
      ),
      okText: 'Disable all',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await disableAllBriefingsForAgent(agentId);
          message.success(
            res.affected === 0
              ? 'No active links to disable'
              : `Disabled ${res.affected} briefing(s)`,
          );
          await load();
        } catch (e: unknown) {
          message.error(e instanceof Error ? e.message : 'Failed to disable');
        }
      },
    });
  }

  // ── Drag-and-drop reorder ────────────────────────────────────────────────
  //
  // Replaces the up/down arrow buttons. Uses @dnd-kit/sortable for accessible
  // keyboard + pointer dragging. The wire call (reorderLinks) is unchanged;
  // we just compute the new id sequence from arrayMove() and ship it.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require 5px of movement before drag fires, so clicking the active
      // toggle / unlink button doesn't accidentally start a drag.
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const next = arrayMove(rows, oldIndex, newIndex);
    setRows(next); // optimistic
    try {
      await reorderLinks(
        agentId,
        next.map((r) => r.id),
      );
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Reorder failed');
      await load();
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  const linkedBriefingIds = new Set(rows.map((r) => r.briefingContextId));

  return (
    <SectionCard noPadding>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 16px',
          borderBottom: `1px solid ${colors.slate[200]}`,
        }}
      >
        <Space size={6} align="center">
          <Title level={5} style={{ margin: 0 }}>
            Patient Briefing
          </Title>
          <HelpHotspot topic="briefing-context.agent-link" />
        </Space>
        <Text type="secondary" style={{ fontSize: 12.5 }}>
          Briefings that compose this agent&apos;s system-prompt patient context.
        </Text>

        <span style={{ flex: 1 }} />

        <Space size={6}>
          <Tag color="default">
            {stats.total} link{stats.total === 1 ? '' : 's'}
          </Tag>
          <Tag color="green">{stats.active} active</Tag>
        </Space>

        <Tooltip title="Reload from server">
          <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading} />
        </Tooltip>
        <Tooltip title="Master kill switch: disable every linked briefing for this agent">
          <Button icon={<PoweroffOutlined />} danger onClick={handleDisableAll} disabled={stats.active === 0}>
            Disable all
          </Button>
        </Tooltip>
        <Tooltip
          title={
            stats.active === 0
              ? 'Link a briefing first, then preview against a patient.'
              : 'Render this agent\'s briefings against a real patient and see exactly what the LLM would receive.'
          }
        >
          <Button
            icon={<EyeOutlined />}
            onClick={() => setPreviewOpen(true)}
            disabled={stats.active === 0}
          >
            Live preview
          </Button>
        </Tooltip>
        <Tooltip title="Ask the AI to read this agent's instructions and recommend briefings to link.">
          <Button icon={<BulbOutlined />} onClick={() => setSuggestOpen(true)}>
            Suggested
          </Button>
        </Tooltip>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
          Link briefing
        </Button>
      </div>

      {stats.orphanedDraft > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ margin: 12 }}
          message={`${stats.orphanedDraft} active link(s) point at a draft or inactive briefing`}
          description="The runtime skips draft and inactive briefings. Either publish/enable the briefing or unlink it here."
        />
      )}

      {rows.length === 0 ? (
        <div style={{ padding: '40px 16px', textAlign: 'center' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div style={{ maxWidth: 360, margin: '0 auto' }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  This agent has no linked briefings yet. Click <strong>Link briefing</strong> to
                  add one — the agent will start receiving its composed patient context on the
                  next turn.
                </Text>
              </div>
            }
          />
        </div>
      ) : (
        <div style={{ padding: 12 }}>
          <Text type="secondary" style={{ fontSize: 11.5, display: 'block', marginBottom: 8, paddingLeft: 4 }}>
            Drag a row to reorder. Lower-position briefings win element-dedup precedence.
          </Text>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rows.map((row) => (
                  <SortableLinkCard
                    key={row.id}
                    row={row}
                    onToggleActive={(next) => void handleToggleActive(row, next)}
                    onUnlink={() => void handleUnlink(row)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      <LinkBriefingDrawer
        open={addOpen}
        briefings={briefings}
        linkedBriefingIds={linkedBriefingIds}
        onClose={() => setAddOpen(false)}
        onAdd={handleAdd}
      />

      <SuggestedBriefingsDrawer
        open={suggestOpen}
        agentId={agentId}
        existingLinkedIds={linkedBriefingIds}
        onClose={() => setSuggestOpen(false)}
        onLinked={async (briefingId) => {
          await handleAdd(briefingId);
        }}
      />

      <LivePreviewDrawer
        open={previewOpen}
        agentId={agentId}
        merchantId={merchantIdForPreview}
        onClose={() => setPreviewOpen(false)}
      />
    </SectionCard>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// SortableLinkCard — one linked briefing as a draggable card
// ─────────────────────────────────────────────────────────────────────────────
//
// Wraps the existing per-link controls (active toggle, unlink) and adds a drag
// handle on the left. The drag handle is the only thing wired into dnd-kit's
// listeners — everything else (toggle clicks, unlink confirm) keeps its normal
// event handling without accidentally starting a drag.

interface SortableLinkCardProps {
  row: JoinedRow;
  onToggleActive: (next: boolean) => void;
  onUnlink: () => void;
}

function SortableLinkCard({ row, onToggleActive, onUnlink }: SortableLinkCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : row.isActive ? 1 : 0.55,
    zIndex: isDragging ? 5 : 'auto',
  };

  const briefingMissing = row.briefingCode == null;
  const briefingStatusMeta = briefingMissing
    ? { tone: 'red' as const, label: 'Missing', tooltip: 'Briefing row not found in the catalog. Consider unlinking.' }
    : !row.briefingIsActive
    ? { tone: 'slate' as const, label: 'Inactive', tooltip: 'Briefing is kill-switched. Runtime skips it.' }
    : row.briefingIsDraft
    ? { tone: 'amber' as const, label: 'Draft', tooltip: 'Briefing is a draft. Runtime skips it until published.' }
    : { tone: 'green' as const, label: 'Published', tooltip: 'Live — agent receives this briefing every turn.' };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        background: '#FFFFFF',
        border: `1px solid ${colors.slate[200]}`,
        borderRadius: 10,
        boxShadow: isDragging ? `0 8px 20px ${colors.slate[300]}55` : '0 1px 2px transparent',
      }}
    >
      {/* Drag handle */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          border: 'none',
          background: 'transparent',
          color: colors.slate[400],
          cursor: 'grab',
          touchAction: 'none',
        }}
      >
        <HolderOutlined style={{ fontSize: 16 }} />
      </button>

      {/* Briefing name + code */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text
          strong
          style={{
            fontSize: 13.5,
            color: colors.slate[900],
            lineHeight: 1.3,
            display: 'block',
          }}
        >
          {row.briefingName || row.briefingCode || `briefing#${row.briefingContextId}`}
        </Text>
        {row.briefingCode && (
          <Text
            type="secondary"
            style={{
              fontFamily: typography.fontMono,
              fontSize: 11.5,
              display: 'block',
              marginTop: 1,
            }}
          >
            {row.briefingCode}
          </Text>
        )}
      </div>

      {/* Status + scope + version */}
      <Space size={6} align="center">
        <Tooltip title={briefingStatusMeta.tooltip}>
          <StatusPill tone={briefingStatusMeta.tone}>{briefingStatusMeta.label}</StatusPill>
        </Tooltip>
        {row.briefingVersion != null && (
          <Text
            type="secondary"
            style={{ fontFamily: typography.fontMono, fontSize: 11 }}
          >
            v{row.briefingVersion}
          </Text>
        )}
        {row.briefingMerchantId == null ? (
          <Tooltip title="Visible to every merchant.">
            <Tag color="blue" style={{ marginRight: 0, fontSize: 10.5 }}>
              Global
            </Tag>
          </Tooltip>
        ) : (
          <Tooltip title={`Private to merchant ${row.briefingMerchantId}.`}>
            <Tag color="geekblue" style={{ marginRight: 0, fontSize: 10.5 }}>
              M{row.briefingMerchantId}
            </Tag>
          </Tooltip>
        )}
      </Space>

      {/* Per-link kill switch */}
      <Tooltip
        title={
          row.isActive
            ? 'Per-link kill switch — turn off to skip just for this agent'
            : 'Re-enable this briefing for the agent'
        }
      >
        <Switch
          size="small"
          checked={row.isActive}
          onChange={onToggleActive}
        />
      </Tooltip>

      {/* Unlink (hard delete) */}
      <Popconfirm
        title="Unlink this briefing?"
        description="Removes the link row entirely. Use the kill switch above instead if you might re-enable later."
        okText="Unlink"
        okButtonProps={{ danger: true }}
        onConfirm={onUnlink}
      >
        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
      </Popconfirm>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LinkBriefingDrawer — row-list picker (consistent with AgentPickerDrawer /
// MerchantPickerDrawer). One click on a row links the briefing and closes.
// No two-step "select then confirm" because linking is trivially reversible
// (unlink button is right there in the parent table).
// ─────────────────────────────────────────────────────────────────────────────

interface LinkDrawerProps {
  open: boolean;
  briefings: BriefingContext[];
  linkedBriefingIds: Set<number>;
  onClose: () => void;
  onAdd: (briefingId: number) => void;
}

function LinkBriefingDrawer({
  open,
  briefings,
  linkedBriefingIds,
  onClose,
  onAdd,
}: LinkDrawerProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<{ focus: () => void } | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    // Focus the search input after the slide-in animation. Mirrors the timing
    // used by AgentPickerDrawer / MerchantPickerDrawer.
    const t = setTimeout(() => inputRef.current?.focus(), 220);
    return () => clearTimeout(t);
  }, [open]);

  // Only published, active briefings are link candidates. Drafts + inactive
  // would be silently skipped at runtime anyway — better to hide them here.
  // Already-linked rows remain visible but get the "Linked" badge + disabled
  // styling so the admin sees the full universe of options at a glance.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return briefings
      .filter((b) => b.isActive && !b.isDraft)
      .filter((b) => {
        if (!q) return true;
        const blob = `${b.code} ${b.name ?? ''} ${b.description ?? ''}`.toLowerCase();
        return blob.includes(q);
      });
  }, [briefings, query]);

  const linkableCount = visible.filter((b) => !linkedBriefingIds.has(b.id)).length;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={520}
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* Sticky header — title, count, search */}
      <div
        style={{
          padding: '16px 20px 12px',
          borderBottom: `1px solid ${colors.slate[200]}`,
          background: '#FFFFFF',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <ReadOutlined style={{ fontSize: 16, color: colors.violet[600] }} />
          <Text strong style={{ fontSize: 15 }}>
            Link a briefing
          </Text>
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 12,
              color: colors.slate[500],
            }}
          >
            {linkableCount} available
          </span>
        </div>

        <Input
          ref={inputRef as never}
          allowClear
          placeholder="Search by code, name, or description…"
          prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPressEnter={() => {
            // If exactly one linkable row, Enter links it immediately —
            // mirrors AgentPickerDrawer's keyboard shortcut.
            const target = visible.find((b) => !linkedBriefingIds.has(b.id));
            if (target && visible.length === 1) onAdd(target.id);
          }}
        />

        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 11.5 }}>
          Drafts and inactive briefings are hidden — the runtime would skip them anyway.
        </Text>
      </div>

      {/* Scrollable row list */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          background: colors.slate[50],
          padding: '8px 0',
        }}
      >
        {visible.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {query
                    ? 'No briefings match your search.'
                    : 'No published briefings yet. Create one on the Briefings page.'}
                </Text>
              }
            />
          </div>
        ) : (
          visible.map((b) => (
            <BriefingRow
              key={b.id}
              briefing={b}
              isLinked={linkedBriefingIds.has(b.id)}
              onClick={() => onAdd(b.id)}
            />
          ))
        )}
      </div>

      {/* Footer — close only; selection is one-click */}
      <div
        style={{
          padding: '10px 20px',
          borderTop: `1px solid ${colors.slate[200]}`,
          background: '#FFFFFF',
          flexShrink: 0,
        }}
      >
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Close</Button>
        </Space>
      </div>
    </Drawer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BriefingRow — single row in the picker. Click anywhere on the row to link.
// ─────────────────────────────────────────────────────────────────────────────

interface BriefingRowProps {
  briefing: BriefingContext;
  isLinked: boolean;
  onClick: () => void;
}

function BriefingRow({ briefing, isLinked, onClick }: BriefingRowProps) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      disabled={isLinked}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        width: '100%',
        padding: '10px 20px',
        background: hover && !isLinked ? '#FFFFFF' : 'transparent',
        border: 'none',
        borderBottom: `1px solid ${colors.slate[100]}`,
        textAlign: 'left',
        cursor: isLinked ? 'default' : 'pointer',
        opacity: isLinked ? 0.55 : 1,
        transition: 'background 120ms ease',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <Text
            style={{
              fontFamily: typography.fontMono,
              fontWeight: 600,
              fontSize: 13,
              color: colors.slate[900],
            }}
          >
            {briefing.code}
          </Text>
          <Text type="secondary" style={{ fontSize: 11.5, fontFamily: typography.fontMono }}>
            v{briefing.version}
          </Text>
          {briefing.merchantId == null ? (
            <Tag color="blue" style={{ marginRight: 0, fontSize: 10.5 }}>
              Global
            </Tag>
          ) : (
            <Tag color="geekblue" style={{ marginRight: 0, fontSize: 10.5 }}>
              M{briefing.merchantId}
            </Tag>
          )}
        </div>
        {briefing.name && (
          <Text
            style={{
              display: 'block',
              fontSize: 12.5,
              color: colors.slate[700],
              marginTop: 2,
            }}
          >
            {briefing.name}
          </Text>
        )}
        {briefing.description && (
          <Text
            type="secondary"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: 12,
              marginTop: 4,
              lineHeight: 1.45,
            }}
          >
            {briefing.description}
          </Text>
        )}
      </div>

      {isLinked && (
        <Tag
          icon={<CheckOutlined />}
          color="success"
          style={{ marginRight: 0, alignSelf: 'center', flexShrink: 0 }}
        >
          Linked
        </Tag>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SuggestedBriefingsDrawer — LLM-ranked recommendations
// ─────────────────────────────────────────────────────────────────────────────
//
// PURPOSE
//   Opens from the "Suggested" toolbar button. On mount, fires one POST to
//   /api/AgentBriefingContext/Suggest/{agentId} which round-trips a small
//   LLM with the agent's instructions + the published-briefings catalog.
//
// STATES
//   - loading: spinner + "reading the agent's instructions…"
//   - error:   inline alert with the server's friendly message (e.g. agent
//              has no Instructions text)
//   - empty:   "no suggestions" empty state with a retry button
//   - data:    ranked cards. Each card: relevance chip, name + code,
//              reasoning paragraph, "Link" button (or muted "Already linked"
//              badge when the briefing is already linked to the agent)
//
// LINKING UX
//   One-click "Link" per card. The drawer optimistically marks the card
//   as linked locally (no re-fetch) and the parent table refreshes on the
//   next load() the parent fires. Multi-link in one drawer session is
//   supported — close when done.
// ─────────────────────────────────────────────────────────────────────────────

interface SuggestedBriefingsDrawerProps {
  open: boolean;
  agentId: number;
  existingLinkedIds: Set<number>;
  onClose: () => void;
  /** Parent handler that actually wires the link + refreshes the list. */
  onLinked: (briefingId: number) => Promise<void>;
}

function SuggestedBriefingsDrawer({
  open,
  agentId,
  existingLinkedIds,
  onClose,
  onLinked,
}: SuggestedBriefingsDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BriefingSuggestionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Local linking state so the cards reflect "just linked" before the parent
  // refreshes. Reset each time the drawer reopens.
  const [justLinkedIds, setJustLinkedIds] = useState<Set<number>>(new Set());
  const [linkingId, setLinkingId] = useState<number | null>(null);

  // Fire the Suggest call exactly once per "open" gesture. We deliberately
  // do NOT include the linked-ids set in the dep array — if the parent
  // re-renders mid-flight (e.g. because the user linked something), we
  // shouldn't issue a second Suggest call.
  useEffect(() => {
    if (!open) {
      // Reset state on close so re-opening starts fresh.
      setResult(null);
      setError(null);
      setJustLinkedIds(new Set());
      setLinkingId(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setResult(null);
    suggestBriefingsForAgent(agentId)
      .then((r) => {
        if (cancelled) return;
        setResult(r);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load suggestions');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentId]);

  async function handleLinkClick(suggestion: BriefingSuggestion) {
    setLinkingId(suggestion.briefingContextId);
    try {
      await onLinked(suggestion.briefingContextId);
      setJustLinkedIds((prev) => {
        const next = new Set(prev);
        next.add(suggestion.briefingContextId);
        return next;
      });
    } finally {
      setLinkingId(null);
    }
  }

  const merged = useMemo(() => {
    if (!result) return [];
    return result.suggestions.map((s) => {
      const wasAlreadyLinked = existingLinkedIds.has(s.briefingContextId)
        || result.alreadyLinkedBriefingIds.includes(s.briefingContextId);
      return {
        suggestion: s,
        isLinked: wasAlreadyLinked || justLinkedIds.has(s.briefingContextId),
      };
    });
  }, [result, existingLinkedIds, justLinkedIds]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={560}
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* Sticky header */}
      <div
        style={{
          padding: '16px 20px 14px',
          borderBottom: `1px solid ${colors.slate[200]}`,
          background: '#FFFFFF',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <BulbOutlined style={{ fontSize: 16, color: colors.violet[600] }} />
          <Text strong style={{ fontSize: 15 }}>
            Suggested briefings
          </Text>
          {result && (
            <span style={{ marginLeft: 'auto', fontSize: 12, color: colors.slate[500] }}>
              {result.suggestions.length} match{result.suggestions.length === 1 ? '' : 'es'}
            </span>
          )}
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Reads this agent&apos;s instructions and ranks every published briefing
          by relevance. No patient data leaves Studio — only agent
          instructions + the public catalog go to the model.
        </Text>
      </div>

      {/* Body — loading / error / empty / data */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          background: colors.slate[50],
          padding: '12px 16px',
        }}
      >
        {loading && (
          <div style={{ padding: '40px 8px', textAlign: 'center' }}>
            <ThunderboltOutlined
              spin
              style={{ fontSize: 22, color: colors.violet[600], marginBottom: 12 }}
            />
            <Text style={{ display: 'block', fontSize: 13 }}>
              Reading the agent&apos;s instructions and the briefings catalog…
            </Text>
            <Text type="secondary" style={{ fontSize: 11.5 }}>
              Typically 3–5 seconds.
            </Text>
          </div>
        )}

        {error && !loading && (
          <Alert
            type="warning"
            showIcon
            message="Could not load suggestions"
            description={error}
            style={{ marginBottom: 12 }}
          />
        )}

        {!loading && !error && result && result.suggestions.length === 0 && (
          <div style={{ padding: '40px 8px', textAlign: 'center' }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div style={{ maxWidth: 360, margin: '0 auto' }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    The AI did not find any strong matches for this agent in the
                    published briefings catalog. Try linking one manually from
                    the <strong>Link briefing</strong> button, or author a new
                    briefing on the Briefings page.
                  </Text>
                </div>
              }
            />
          </div>
        )}

        {!loading && !error && merged.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {merged.map(({ suggestion, isLinked }) => (
              <SuggestionCard
                key={suggestion.briefingContextId}
                suggestion={suggestion}
                isLinked={isLinked}
                isLinking={linkingId === suggestion.briefingContextId}
                onLink={() => void handleLinkClick(suggestion)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer — provenance + close */}
      <div
        style={{
          padding: '10px 20px',
          borderTop: `1px solid ${colors.slate[200]}`,
          background: '#FFFFFF',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Text type="secondary" style={{ fontSize: 11, flex: 1 }}>
          {result
            ? `${result.modelUsed ? `Model: ${result.modelUsed}` : 'Model: default'} · ${(result.elapsedMs / 1000).toFixed(1)}s`
            : ''}
        </Text>
        <Button onClick={onClose}>Close</Button>
      </div>
    </Drawer>
  );
}

// One ranked-recommendation card. Layout: relevance chip on the left,
// name + code + reasoning in the middle, Link button on the right.

interface SuggestionCardProps {
  suggestion: BriefingSuggestion;
  isLinked: boolean;
  isLinking: boolean;
  onLink: () => void;
}

function SuggestionCard({ suggestion, isLinked, isLinking, onLink }: SuggestionCardProps) {
  const relevanceTone: 'green' | 'amber' | 'slate' =
    suggestion.relevance >= 8 ? 'green' : suggestion.relevance >= 6 ? 'amber' : 'slate';
  const relevanceColor =
    relevanceTone === 'green' ? colors.green[600]
    : relevanceTone === 'amber' ? colors.amber[600]
    : colors.slate[500];
  const relevanceBackground =
    relevanceTone === 'green' ? colors.green[100]
    : relevanceTone === 'amber' ? colors.amber[100]
    : colors.slate[100];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 14px',
        background: '#FFFFFF',
        border: `1px solid ${colors.slate[200]}`,
        borderRadius: 10,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      }}
    >
      {/* Relevance chip */}
      <Tooltip
        title={
          suggestion.relevance >= 8
            ? 'Strong match — the agent\'s job is clearly improved by this briefing.'
            : suggestion.relevance >= 6
            ? 'Moderate match — useful but not core to the agent\'s job.'
            : 'Marginal match — surfaced for completeness; review carefully.'
        }
      >
        <div
          aria-label={`Relevance ${suggestion.relevance} of 10`}
          style={{
            flexShrink: 0,
            minWidth: 38,
            height: 38,
            borderRadius: 8,
            background: relevanceBackground,
            color: relevanceColor,
            fontWeight: 700,
            fontSize: 14,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {suggestion.relevance}
        </div>
      </Tooltip>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <Text strong style={{ fontSize: 13.5, color: colors.slate[900] }}>
            {suggestion.name || suggestion.code}
          </Text>
          <Text
            type="secondary"
            style={{ fontFamily: typography.fontMono, fontSize: 11.5 }}
          >
            {suggestion.code}
          </Text>
        </div>
        {suggestion.reasoning && (
          <Text
            style={{
              display: 'block',
              fontSize: 12.5,
              color: colors.slate[700],
              marginTop: 4,
              lineHeight: 1.5,
            }}
          >
            {suggestion.reasoning}
          </Text>
        )}
      </div>

      {/* Action */}
      <div style={{ flexShrink: 0, alignSelf: 'center' }}>
        {isLinked ? (
          <Tag
            icon={<CheckOutlined />}
            color="success"
            style={{ marginRight: 0, fontSize: 11 }}
          >
            Linked
          </Tag>
        ) : (
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            loading={isLinking}
            onClick={onLink}
          >
            Link
          </Button>
        )}
      </div>
    </div>
  );
}
